<template>
  <VModal ref="modal" @modal:hidden="$emit('hide')">
    <template #default="modalProps">
      <div class="rounded shadow-xs w-full max-w-full h-full mx-auto">
        <div class="rounded shadow-lg bg-gray-100 overflow-hidden h-full flex flex-col">
          <div class="px-4 sm:px-6 py-5 border-b flex-none">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h1 class="text-sm">Facebook Campaign Performance</h1>
              </div>

              <div class="flex-none">
                <button
                  type="button"
                  class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="modalProps.close"
                >
                  <span class="sr-only">Close</span>
                  <svg
                    class="inline-flex h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="facebookCampaign"
            class="px-4 sm:px-6 py-5 flex-none bg-white border-b flex justify-between items-center"
          >
            <h3 class="text-xl font-bold">
              {{ facebookCampaign.name }}
            </h3>
            <BaseDatePicker
              v-model="insightsRange"
              :default-label="datePickerLabel"
              class="inline-flex"
            >
              <template #default="{ shown }">
                <div
                  class="inline-flex select-none rounded-md border border-gray-300 px-3 py-2 cursor-pointer"
                  :class="[shown ? '' : 'hover:bg-gray-100 hover:border-blue-600']"
                >
                  <div class="flex gap-3 items-center">
                    <div class="flex-1">
                      <span for="name" class="block text-xs font-medium text-gray-900 text-right">{{
                        $t('global.period')
                      }}</span>
                      <span
                        v-text="datePickerLabel"
                        class="block w-full border-0 p-0 text-gray-900 placeholder-gray-500 sm:text-sm"
                      />
                    </div>
                    <div class="flex-none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="w-4 h-4"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </template>
            </BaseDatePicker>
          </div>
          <div class="flex-1 bg-white overflow-auto p-4">
            <div v-if="loading" class="flex h-full items-center justify-center"><Loader /></div>
            <div v-else-if="facebookCampaign" class="space-y-4">
              <ChartsSection :insights="insights" />
              <PerformanceWidget :insights="insights" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </VModal>
</template>

<script>
import moment from 'moment';
import { accountFacebookCampaignsClient } from '@/api/clients/index';
import ChartsSection from './ChartsSection.vue';
import PerformanceWidget from './PerformanceWidget.vue';

export default {
  components: {
    ChartsSection,
    PerformanceWidget,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    facebookCampaignId: {
      type: String,
      required: true,
    },
    insightsSince: {
      type: String,
      default: '',
    },
    insightsUntil: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: true,
      facebookCampaign: null,
      insightsRange:
        this.insightsSince && this.insightsUntil ? [this.insightsSince, this.insightsUntil] : [],
    };
  },
  computed: {
    datePickerLabel() {
      if (this.insightsRange.length === 0) {
        return `${this.$t('global.beginOfTimeUntil')} ${moment().format('DD-MMM-YYYY')}`;
      }

      const [since, until] = this.insightsRange.map((date) => moment(date));

      if (since.isSame(until)) {
        return since.format('DD-MMM-YYYY');
      }

      return [
        this.$t('global.periodFrom'),
        since.format('DD-MMM-YYYY'),
        this.$t('global.periodΤο'),
        until.format('DD-MMM-YYYY'),
      ].join(' ');
    },
    insightsByTime() {
      return (this.facebookCampaign?.facebook_campaign_insights || []).reduce(
        (carry, current) => ({
          ...carry,
          [moment(current.date_start).startOf('day').valueOf()]: current,
        }),
        {}
      );
    },
    timeSlots() {
      const timestamps = Object.keys(this.insightsByTime);

      if (timestamps.length === 0) {
        return [];
      }

      const [since] = timestamps;
      const [until] = timestamps.reverse();
      const current = moment(Number.parseInt(since)).startOf('day');
      const limit = moment(Number.parseInt(until)).startOf('day');
      const dates = [];
      do {
        dates.push(current.valueOf());
        current.add(1, 'days');
      } while (limit - current > -1);

      return dates.sort((a, b) => a > b);
    },
    insights() {
      return this.timeSlots.map((time) => {
        const { [time]: data = null } = this.insightsByTime;
        return {
          time,
          data,
        };
      });
    },
  },
  watch: {
    insightsRange() {
      this.fetchFacebookCampaign();
    },
  },
  created() {
    this.fetchFacebookCampaign();
  },
  methods: {
    fetchFacebookCampaign() {
      accountFacebookCampaignsClient
        .read(this.accountId, this.facebookCampaignId, {
          include: 'facebook_campaign_insights,facebook_campaign_insights.contact_requests_count',
          ...(this.insightsRange.length === 2
            ? {
                filter: {
                  'insights.since': this.insightsRange[0],
                  'insights.until': this.insightsRange[1],
                },
              }
            : {}),
        })
        .then((r) => {
          this.facebookCampaign = r.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
